/* eslint-disable no-unused-vars */
import { useEffect } from 'react';
import useSnackbar from 'src/hooks/useCustomSnackbar';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@mui/material';
import * as Sentry from "@sentry/react";
// redux
import { dispatch, useSelector } from '../redux/store';
import { updateError } from '../redux/slices/user';
import { leadPage } from '../redux/slices/lead';
//
import { Iconify } from '.';
import isPublicRoute from '../utils/isPublicRoute';

// Cascade detector: when an auth failure blows up multiple unrelated API
// calls at once, they all land here in rapid succession. We let the first
// CASCADE_THRESHOLD events through to Sentry and suppress the rest while
// the window is still open so a single auth failure doesn't produce 8+
// duplicate issues.
let lastErrorTime = 0;
let errorCountInWindow = 0;
const CASCADE_WINDOW_MS = 1000;
const CASCADE_THRESHOLD = 3;

export default function ErrorHandler() {
    const { error } = useSelector(state => state.user);
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    const { practiceId } = useSelector((state) => state.practiceTreatmentList);

    const navigate = useNavigate();
    const { pathname } = useLocation();

    const handleViewLeadDetail = (key, id) => {
        closeSnackbar(key);
        dispatch(leadPage({}));
        navigate(`/dashboard/practice/${practiceId}/lead-detail/${id}`, { state: { shouldRedirectToOverview: true } });
    };

    useEffect(() => {
        // Suppress all snackbar notifications on patient-facing public pages
        if (isPublicRoute(pathname)) return;

        if (error !== null) {
            const AUTH_ERROR_PATTERNS = [
                'token is invalid or expired',
                'token is blacklisted',
                'given token not valid',
                'authentication credentials were not provided',
                'session has timed out',
                'refresh token not valid',
                'no active account found',
                'user inactive',
                // Refresh token validation — DRF returns {"refresh":["This field may not be null."]}
                // when localStorage refreshToken is null/expired. The apiCall.js refresh handler
                // already shows "Session has timed Out!" and redirects — this prevents the raw
                // JSON from also appearing as a second toast from the calling thunk.
                '"refresh"',
                'this field may not be null',
            ];
            const rawErrValue = error.message?.detail || error.message || '';
            const rawErrorMsg = typeof rawErrValue === 'string' ? rawErrValue : JSON.stringify(rawErrValue);
            const isAuthStatus = error?.status === 401 ||
                (error?.status === 403 && (rawErrorMsg.toLowerCase().includes('permission') || rawErrorMsg.toLowerCase().includes('credentials')));
            const isAuthError = isAuthStatus || AUTH_ERROR_PATTERNS.some(
                p => rawErrorMsg.toLowerCase().includes(p)
            );

            // Cascade detector — auth failures often blow up 4-10 unrelated
            // API calls simultaneously. Let the first CASCADE_THRESHOLD
            // through, suppress the rest within the window.
            const now = Date.now();
            if (now - lastErrorTime < CASCADE_WINDOW_MS) {
                errorCountInWindow += 1;
            } else {
                errorCountInWindow = 1;
            }
            lastErrorTime = now;
            const isInCascade = errorCountInWindow > CASCADE_THRESHOLD;

            const isNotFound = error.status === 404 || rawErrorMsg.includes('Not found.') || rawErrorMsg === '{"detail":"Not found."}';
            // Narrow the archived-lead heuristic.
            //
            // The previous check matched any string containing
            // "object does not exist" or "invalid pk" — which is the *generic*
            // DRF PrimaryKeyRelatedField failure message. That made the
            // "This lead has been archived" snackbar fire on every unrelated
            // FK validation error (e.g. the form-builder create call failing
            // because the FormFieldType lookup row was missing).
            //
            // A real archived-lead error either:
            //   * comes from a thunk that explicitly attaches ``error.lead``
            //     (a lead id, used to render the "Show lead" button), OR
            //   * includes the word "lead" in the BE error string itself.
            // Require at least one of those signals *in addition to* the
            // FK-style phrase before relabelling the user-visible message.
            const looksLikeFkError = rawErrorMsg.toLowerCase().includes('object does not exist')
                || rawErrorMsg.toLowerCase().includes('invalid pk');
            const mentionsLead = rawErrorMsg.toLowerCase().includes('lead') || Boolean(error?.lead);
            const isArchivedLead = looksLikeFkError && mentionsLead;
            let errorMsg = rawErrorMsg;
            // Guard against JSON.stringify(null) → "null" from thunks using
            // `JSON.stringify(response?.data?.message) || 'Something went wrong'`
            if (errorMsg === 'null' || errorMsg === 'undefined' || errorMsg === '"null"') {
                errorMsg = 'Something went wrong';
            }
            if (isArchivedLead) errorMsg = 'This lead has been archived by another user.';
            else if (isNotFound) errorMsg = 'This record could not be found — it may have been deleted or moved.';

            const EXPECTED_USER_MESSAGES = [
                'please select tab',
                'not available for communication',
                'please add archive notes',
                'please select an archive reason',
                'unable to proceed',
            ];
            const isExpectedMessage = EXPECTED_USER_MESSAGES.some(
                p => rawErrorMsg.toLowerCase().includes(p)
            );

            if (error?.success === "warning") {
                enqueueSnackbar(error?.message ?? "", {
                    variant: 'warning',
                    anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'center'
                    }
                });
            }
            else if (error?.success !== 'success' && !error?.success) {
                if (!isAuthError && !isNotFound && !isArchivedLead && !isExpectedMessage && !isInCascade) {
                    const customError = new Error(errorMsg);
                    customError.name = "UI Popup Error";
                    Sentry.captureException(customError, {
                        fingerprint: [
                            'ui-popup-error',
                            // First 80 chars of message (deduplicates dynamic IDs in messages)
                            (errorMsg || 'unknown').toString().slice(0, 80),
                            // HTTP status if available
                            String(error?.status ?? 'no-status'),
                        ],
                        tags: {
                            feature: "Handled Exception - Possible UI Snackbar",
                            ...(error?.status && { http_status: error.status }),
                        },
                        extra: {
                            debugInfo: JSON.stringify(error),
                        },
                    });
                }
            }

            if (isAuthError) {
                dispatch(updateError(null));
                return;
            }

            if (!errorMsg) return; // Don't show empty snackbars
            enqueueSnackbar(errorMsg, {
                variant: error?.success ? 'success' : 'error', anchorOrigin: { vertical: 'top', horizontal: 'center' }, autoHideDuration: error.timeout, id: 'success-notification',
                action: (key) =>
                (error.lead ? <>
                    <Button onClick={() => handleViewLeadDetail(key, error.lead)} variant='outlined' size="small"  >
                        Show lead <Iconify icon={'ep:user-filled'} height={15} width={15} sx={{ margin: '0px 8px', cursor: 'pointer' }} />
                    </Button>
                    <Iconify onClick={() => closeSnackbar(key)} icon={'radix-icons:cross-2'} height={15} width={15} sx={{ margin: '0px 8px', cursor: 'pointer' }} />
                </> :
                    <Iconify onClick={() => closeSnackbar(key)} icon={'radix-icons:cross-2'} height={15} width={15} sx={{ margin: '0px 8px', cursor: 'pointer' }} />
                )
            }, true);
            setTimeout(() => {
                dispatch(updateError(null));
            }, 3000);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error]);
    return (
        <></>
    );
}